import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaMapMarkedAlt, FaBolt, FaCog } from 'react-icons/fa';

function formatChargingScenarioLabel(mode) {
    const key = String(mode || '').trim().toLowerCase();
    if (key === 'active') return 'Active Managed';
    if (key === 'passive') return 'Passive Managed';
    if (key === 'unmanaged') return 'Unmanaged';
    return key ? String(mode).trim() : 'Unmanaged';
}

function buildCaseDisplayTitle(caseData) {
    const utilityName = String(caseData?.utility_name || '').trim() || 'Utility';
    const rawScenario = String(caseData?.forecast_scenario || '').trim();
    const forecastScenario = rawScenario && rawScenario !== 'MDHD load forecast'
        ? rawScenario
        : 'Class 7-8 HD';
    const chargingScenario = String(caseData?.charging_scenario || '').trim();
    const chargingMode = caseData?.input_data?.mode || caseData?.result_data?.simulation_params?.mode;
    return `${utilityName} ${forecastScenario} ${chargingScenario || formatChargingScenarioLabel(chargingMode)}`;
}

export const UtilityScenarioCaseCard = ({ caseData, onDelete }) => {
    const navigate = useNavigate();
    const displayTitle = buildCaseDisplayTitle(caseData);

    return (
        <div 
            className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden border border-gray-200 hover:border-blue-400 font-ui"
            onClick={() => navigate(`/utility/scenarios/cases/${caseData.id}`)}
        >
            {/* Header - white background with black text */}
            <div className="bg-white p-4">
                <h3 className="text-lg font-bold mb-1 truncate text-black">{displayTitle}</h3>
                <div className="flex items-center gap-2 text-sm text-black">
                    <FaMapMarkedAlt className="flex-shrink-0 text-black" />
                    <span className="truncate text-black">{caseData.region}</span>
                </div>
            </div>

            {/* Body */}
            <div className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                        {caseData.forecast_scenario || 'MDHD load forecast'}
                    </span>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                    <div className="bg-blue-50 rounded p-2">
                        <div className="flex items-center gap-1 mb-1">
                            <FaBolt className="text-blue-600 text-xs" />
                            <p className="text-xs font-semibold text-gray-700">Final Load</p>
                        </div>
                        <p className="text-lg font-bold text-blue-600">{Number(caseData.total_load_mwh || 0).toFixed(2)} MWh</p>
                    </div>
                    <div className="bg-purple-50 rounded p-2">
                        <div className="flex items-center gap-1 mb-1">
                            <FaCog className="text-purple-600 text-xs" />
                            <p className="text-xs font-semibold text-gray-700">Hexagons</p>
                        </div>
                        <p className="text-lg font-bold text-purple-600">{caseData.hexagon_count ?? 0}</p>
                    </div>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded p-2">
                    <p className="text-xs text-gray-800">
                        <span className="font-semibold">Utility:</span> {caseData.utility_name || '—'}
                    </p>
                    <p className="text-xs text-gray-800">
                        <span className="font-semibold">Status:</span> {caseData.status || 'completed'}
                    </p>
                </div>

                <button 
                    className="w-full mt-2 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded transition-colors duration-200"
                    onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/utility/scenarios/cases/${caseData.id}`);
                    }}
                >
                    View Details
                </button>
                <button
                    className="w-full py-2 border border-red-200 text-red-700 hover:bg-red-50 text-sm font-semibold rounded transition-colors duration-200"
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete?.(caseData);
                    }}
                >
                    Delete Case
                </button>
            </div>
        </div>
    );
};
